import { withTheme } from '@digital-retail/falabella-ui-cross-components';
import PropTypes from 'prop-types';
import useSizeButton from '../hooks/useSizeButton';
import { SizeButtonStyles } from './style';

/**
 * SizeButton v2 - Modern BEM-based size selector component
 *
 * Features:
 * - BEM CSS methodology for better maintainability
 * - Enhanced accessibility with ARIA attributes
 * - Modern styling with improved hover and focus states
 * - Responsive design optimized for touch devices
 * - High contrast and reduced motion support
 *
 * @param {Object} props - Component props
 * @param {string} props.value - Size value (e.g., "S", "M", "L", "XL")
 * @param {boolean} props.available - Whether the size is available for selection
 * @param {string} props.index - Size index identifier
 * @param {string} props.selectedSize - Currently selected size value
 * @param {Function} props.onSizeChange - Callback function when size is changed
 * @param {Object} props.theme - Theme object for styling
 * @param {string} props.enhancedSizeSelectorClassName - Additional CSS class name
 */
const SizeButton = ({ value, available, index, selectedSize, onSizeChange, theme, enhancedSizeSelectorClassName }) => {
  const { handleClick, isSelected, themedStyles, buttonId, isDisabled } = useSizeButton({
    value,
    available,
    index,
    selectedSize,
    onSizeChange,
    theme,
    enhancedSizeSelectorClassName,
  });

  // BEM class generation
  const bemClasses = [
    'size-button-v2',
    enhancedSizeSelectorClassName && `size-button-v2--${enhancedSizeSelectorClassName}`,
    isSelected && 'size-button-v2--selected',
    !available && 'size-button-v2--unavailable',
  ]
    .filter(Boolean)
    .join(' ');

  const v2Styles = SizeButtonStyles(theme);

  return (
    <button
      id={buttonId}
      type="button"
      className={bemClasses}
      onClick={handleClick}
      disabled={isDisabled}
      aria-pressed={isSelected}
      aria-label={`Size ${value}${isSelected ? ' (selected)' : ''}${!available ? ' (unavailable)' : ''}`}
      tabIndex={0}
    >
      <span className="size-button-v2__text">{value}</span>
      <style jsx>{themedStyles}</style>
      <style jsx>{v2Styles}</style>
    </button>
  );
};

SizeButton.defaultProps = {
  theme: null,
  enhancedSizeSelectorClassName: '',
};

SizeButton.propTypes = {
  value: PropTypes.string.isRequired,
  available: PropTypes.bool.isRequired,
  index: PropTypes.string.isRequired,
  selectedSize: PropTypes.string.isRequired,
  onSizeChange: PropTypes.func.isRequired,
  theme: PropTypes.object,
  enhancedSizeSelectorClassName: PropTypes.string,
};

export { SizeButton };
export default withTheme(SizeButton);
